/**
 * useStrategyVersionActions — strategy-canvas version management (Spec A / D51).
 *
 * Mirrors the version-management surface of `useOnboardingEditorActionsV2`
 * (versions list + loading/error/success + switch/create), but drives the
 * STRATEGY version axis: a strategy's working head is mutable while
 * `status='draft'`; an explicit **Bake** snapshots it into an immutable version.
 *
 * Unlike the onboarding hook, "switching version" here does NOT re-address the
 * live coordinator (the BlockFlowCoordinator reads the mutable head only).
 * Read-only viewing / diff / cherry-pick operate over reconstructed snapshots
 * (`StrategyVersionService.getVersionSnapshot`) — see StrategyVersionSwitcher.
 *
 * Anti-spam contract (Q4): repeated draft edits mint NO version — only Bake does.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { StrategyVersionService } from '@modules/firebase-core/services/StrategyVersionService';
import type { StrategyVersionSnapshot } from '@modules/firebase-core/services/StrategyVersionService';
import { StrategyService } from '@modules/firebase-core/services/StrategyService';
import type { StrategyVersion, StrategyStatus } from '@segmently/schema-models/strategy';

export interface UseStrategyVersionActionsProps {
  projectId: string;
  strategyId: string;
  /** Set true only when the canvas is open (avoids reads while closed). */
  enabled?: boolean;
  /** Bump to force a re-read of status/versions after an external mutation. */
  refreshToken?: number;
}

export interface UseStrategyVersionActionsReturn {
  // Version state
  versions: StrategyVersion[];
  status: StrategyStatus;
  currentVersion: string;
  loadingVersions: boolean;

  // Operations
  refresh: () => Promise<void>;
  bake: (name?: string, description?: string) => Promise<StrategyVersion | null>;
  startNewVersion: (fromVersionId?: string) => Promise<void>;
  cherryPickBlock: (fromVersionId: string, blockId: string) => Promise<string | null>;
  getSnapshot: (versionId: string) => Promise<StrategyVersionSnapshot | null>;

  // Status
  loading: boolean;
  error: string | null;
  clearError: () => void;
  successMessage: string | null;
  clearSuccess: () => void;
}

export function useStrategyVersionActions({
  projectId,
  strategyId,
  enabled = true,
  refreshToken = 0,
}: UseStrategyVersionActionsProps): UseStrategyVersionActionsReturn {
  const versionService = useMemo(() => new StrategyVersionService(projectId), [projectId]);
  const strategyService = useMemo(() => new StrategyService(projectId), [projectId]);

  const [versions, setVersions] = useState<StrategyVersion[]>([]);
  const [status, setStatus] = useState<StrategyStatus>('draft');
  const [currentVersion, setCurrentVersion] = useState<string>('');
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!enabled || !projectId || !strategyId) return;
    setLoadingVersions(true);
    try {
      const [list, strategy] = await Promise.all([
        versionService.listVersions(strategyId),
        strategyService.get(strategyId),
      ]);
      setVersions(list);
      // A legacy strategy with no lifecycle yet reads as a draft (matches A8 migration default).
      setStatus(((strategy as { status?: StrategyStatus })?.status) ?? 'draft');
      setCurrentVersion(((strategy as { currentVersion?: string })?.currentVersion) ?? '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load strategy versions');
    } finally {
      setLoadingVersions(false);
    }
  }, [enabled, projectId, strategyId, versionService, strategyService]);

  useEffect(() => {
    void load();
  }, [load, refreshToken]);

  const refresh = useCallback(async () => {
    await load();
  }, [load]);

  const bake = useCallback(
    async (name?: string, description?: string): Promise<StrategyVersion | null> => {
      setLoading(true);
      setError(null);
      try {
        const uid = getAuth().currentUser?.uid;
        const version = await versionService.bake(strategyId, { name, description, bakedByUid: uid });
        await load();
        setSuccessMessage(`Baked ${version.name ?? 'version'}`);
        return version;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to bake strategy');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [versionService, strategyId, load],
  );

  const startNewVersion = useCallback(
    async (fromVersionId?: string): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        await versionService.startNewVersion(strategyId, fromVersionId);
        await load();
        setSuccessMessage(
          fromVersionId
            ? 'Started a new draft from the selected version'
            : 'Strategy reopened for editing',
        );
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to start a new version');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [versionService, strategyId, load],
  );

  const cherryPickBlock = useCallback(
    async (fromVersionId: string, blockId: string): Promise<string | null> => {
      setLoading(true);
      setError(null);
      try {
        const newBlockId = await versionService.cherryPickBlock(strategyId, fromVersionId, blockId);
        await load();
        setSuccessMessage('Block cherry-picked into the working draft');
        return newBlockId;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to cherry-pick block');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [versionService, strategyId, load],
  );

  const getSnapshot = useCallback(
    (versionId: string) => versionService.getVersionSnapshot(strategyId, versionId),
    [versionService, strategyId],
  );

  const clearError = useCallback(() => setError(null), []);
  const clearSuccess = useCallback(() => setSuccessMessage(null), []);

  return {
    versions,
    status,
    currentVersion,
    loadingVersions,
    refresh,
    bake,
    startNewVersion,
    cherryPickBlock,
    getSnapshot,
    loading,
    error,
    clearError,
    successMessage,
    clearSuccess,
  };
}
